import React, { useMemo } from "react"
import { parsedUser } from "@/common/GetCurrentUser"

interface MentionRendererProps {
  text: string
  className?: string
}

/**
 * Rich Mention Renderer:
 * Parses text and highlights `@Usuario` with a modern pill badge.
 * Highlights if the current user is mentioned.
 */
export const MentionRenderer: React.FC<MentionRendererProps> = ({ text, className = "" }) => {
  const currentUser = parsedUser()
  const currentUserName = useMemo(() => {
    if (!currentUser) return ""
    return `${currentUser.FirstName || ""} ${currentUser.LastName || ""}`.trim().toLowerCase()
  }, [currentUser])

  const parsedContent = useMemo(() => {
    if (!text) return null

    // Regex handles both formatted mention token `@[Name](id)` and plain `@First Last` or `@Word`
    // Matches @[Name](id) or @Name (allowing alphanumeric and accented characters, spaces up to 2 words)
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)|@([a-zA-ZÀ-ÿ0-9_]+(?:\s+[a-zA-ZÀ-ÿ0-9_]+)?)/g

    const elements: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = mentionRegex.exec(text)) !== null) {
      const matchIndex = match.index

      // Push text preceding the mention
      if (matchIndex > lastIndex) {
        elements.push(text.slice(lastIndex, matchIndex))
      }

      const mentionName = (match[1] || match[3] || "").trim()
      const mentionId = match[2] || ""
      const isMe =
        (mentionId && String(mentionId) === String(currentUser?.ID)) ||
        (currentUserName && mentionName.toLowerCase() === currentUserName)

      elements.push(
        <span
          key={`mention-${matchIndex}`}
          className={`d-inline-flex align-items-center gap-1 px-1.5 py-0.5 rounded-pill fw-semibold me-1 ${
            isMe
              ? "bg-warning bg-opacity-25 text-dark border border-warning"
              : "bg-primary-subtle text-primary border border-primary-subtle"
          }`}
          style={{
            fontSize: "0.85em",
            verticalAlign: "baseline",
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}
          title={isMe ? "¡Te mencionaron a ti!" : `Mención a ${mentionName}`}
        >
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "14px",
              height: "14px",
              fontSize: "0.68em",
              backgroundColor: isMe ? "#f59e0b" : "#0284c7",
              color: "#fff",
            }}
          >
            @
          </span>
          <span className="text-truncate" style={{ maxWidth: "160px" }}>
            {mentionName}
          </span>
          {isMe && (
            <span
              className="badge bg-warning text-dark px-1 py-0 ms-0.5 rounded-pill"
              style={{ fontSize: "0.65em" }}
            >
              Tú
            </span>
          )}
        </span>
      )

      lastIndex = mentionRegex.lastIndex
    }

    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex))
    }

    return elements
  }, [text, currentUser?.ID, currentUserName])

  return <span className={`mention-rendered-text ${className}`}>{parsedContent}</span>
}

export default MentionRenderer
